/**
 * FileMakerXMLToClipboard.ts - Clippr plugin for FMwvTool
 * 
 * Sets FileMaker XML to clipboard as pastable FileMaker objects
 * Uses cross-platform clipboard API (browser Clipboard API or Electron)
 */

import type { Plugin } from '../../PluggrTypes';

export class FileMakerXMLToClipboard {
  
  /**
   * Copy FileMaker XML to clipboard as pastable FM objects
   * 
   * In browser: Uses Clipboard API with custom MIME type
   * In Electron: Could use native clipboard with proper format
   */
  async copyToClipboard(xmlContent: string): Promise<void> {
    // Check if we're in a browser environment
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      await this.copyToBrowserClipboard(xmlContent);
    } else {
      // Fallback or Electron implementation
      throw new Error('Clipboard API not available');
    }
  }
  
  /**
   * Copy to browser clipboard
   * 
   * Note: FileMaker's clipboard format is proprietary, so we store as:
   * 1. Plain text (the XML)
   * 2. Custom MIME type for FileMaker
   */
  private async copyToBrowserClipboard(xmlContent: string): Promise<void> {
    try {
      // Create a ClipboardItem with multiple representations
      const blob = new Blob([xmlContent], { type: 'text/plain' });
      const fmBlob = new Blob([xmlContent], { type: 'application/x-filemaker-xml' });
      
      const clipboardItem = new ClipboardItem({
        'text/plain': blob,
        'application/x-filemaker-xml': fmBlob
      });
      
      await navigator.clipboard.write([clipboardItem]);
    } catch (error) {
      // Fallback to simple text copy
      await navigator.clipboard.writeText(xmlContent);
    }
  }
  
  /**
   * Get XML from clipboard
   */
  async getFromClipboard(): Promise<string> {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      return await navigator.clipboard.readText();
    }
    throw new Error('Clipboard API not available');
  }
  
  /**
   * Check if clipboard contains FileMaker XML
   */
  async hasFileMakerXML(): Promise<boolean> {
    try {
      const text = await this.getFromClipboard();
      return text.includes('fmxmlsnippet') && text.includes('<?xml');
    } catch {
      return false;
    }
  }
}

// Plugin definition
export const fileMakerXMLToClipboardPlugin: Plugin = {
  manifest: {
    id: 'filemaker-xml-to-clipboard',
    name: 'FileMaker XML to Clipboard',
    version: '1.0.0',
    description: 'Copy FileMaker XML to clipboard as pastable FM objects',
    author: 'Trusted Associates',
    main: 'FileMakerXMLToClipboard.ts',
    category: 'filemaker',
    tags: ['filemaker', 'clipboard', 'xml', 'clippr'],
  },
  
  // Plugin lifecycle
  onload: async (context) => {
    const clipper = new FileMakerXMLToClipboard();
    
    // Register commands
    context.registerCommand?.({
      id: 'copy-xml-to-clipboard',
      name: 'Copy FM XML to Clipboard',
      description: 'Copy FileMaker XML to clipboard as pastable objects',
      execute: async (xmlContent: string) => {
        await clipper.copyToClipboard(xmlContent);
        context.ui?.showNotification('FileMaker XML copied to clipboard!', 'success');
        return true;
      }
    });
    
    context.registerCommand?.({
      id: 'get-xml-from-clipboard',
      name: 'Get FM XML from Clipboard',
      description: 'Get FileMaker XML from clipboard',
      execute: async () => {
        return await clipper.getFromClipboard();
      }
    });
    
    context.registerCommand?.({
      id: 'check-clipboard-has-xml',
      name: 'Check if Clipboard has FM XML',
      description: 'Check if clipboard contains FileMaker XML',
      execute: async () => {
        return await clipper.hasFileMakerXML();
      }
    });
    
    context.log?.info('FileMaker XML to Clipboard plugin loaded');
  },
  
  onunload: async () => {
    // Cleanup if needed
  }
};
